import React, { useState } from "react";
import { CheckCircle2, ChevronDown, Calendar } from "lucide-react";
import { siteConfig } from "../data/mockData";

const qualificationStatements = [
  "You're self-employed, freelance, or running your own business — and figuring out health coverage on your own has felt confusing or overwhelming.",
  "You want real coverage that doesn't leave gaps — not just the cheapest plan, but the right one for your health, your budget, and how you actually live.",
  "You'd rather spend 30 minutes with someone who knows the market than hours comparing plans on your own and still not feeling sure.",
];

const coverageItems = [
  {
    id: "small-business",
    title: "Small Business — Employees & Contractors",
    subtitle: "I have people to cover",
    body: "Group health plans, ICHRA, and supplemental options that help you offer real benefits to your team without the corporate price tag. Whether you have 2 employees or 50, I'll find a plan that fits your budget and keeps your people covered.",
  },
  {
    id: "families",
    title: "Families — Household",
    subtitle: "I need coverage for my household too",
    body: "Comprehensive family coverage that protects everyone under your roof — preventive care, specialist visits, prescriptions, dental and vision. I'll help you find a plan that works for every age and stage of life.",
  },
  {
    id: "individuals",
    title: "Individuals — Personal or In-Between",
    subtitle: "Just need personal coverage",
    body: "Whether you're between jobs, recently self-employed, aging off a parent's plan, or just want better coverage than what's on the marketplace, I'll match you with a plan that fits your health, budget, and lifestyle.",
  },
];

const CoverageAccordionItem = ({ item, isOpen, onToggle }) => (
  <div
    data-testid={`coverage-item-${item.id}`}
    className="bg-white rounded-xl border border-gray-100 overflow-hidden hover:border-[#3A7D44]/20 transition-colors"
  >
    <button
      onClick={onToggle}
      data-testid={`coverage-toggle-${item.id}`}
      className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left hover:bg-[#F1F8F2]/50 transition-colors"
      aria-expanded={isOpen}
    >
      <div className="flex-1">
        <h3 className="text-lg font-bold text-[#1B2A1B]">{item.title}</h3>
        <p className="text-sm text-[#5A6B5A] mt-1">{item.subtitle}</p>
      </div>
      <ChevronDown
        size={22}
        className={`text-[#3A7D44] flex-shrink-0 transition-transform duration-300 ${
          isOpen ? "rotate-180" : ""
        }`}
      />
    </button>

    <div
      className={`overflow-hidden transition-all duration-300 ${
        isOpen ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"
      }`}
    >
      <div className="px-6 pb-6 pt-1 border-t border-[#F1F8F2]">
        <p className="text-sm sm:text-base text-[#5A6B5A] leading-relaxed mt-4 mb-5">
          {item.body}
        </p>
        <a
          href={siteConfig.calendlyUrl}
          data-testid={`coverage-cta-${item.id}`}
          className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-[#3A7D44] rounded-lg hover:bg-[#2D5A35] transition-colors"
        >
          <Calendar size={16} />
          Book a Call
        </a>
      </div>
    </div>
  </div>
);

const CoverageAccordion = () => {
  const [openId, setOpenId] = useState(null);

  return (
    <div className="max-w-3xl mx-auto mt-16">
      <p className="text-sm font-semibold text-[#3A7D44] uppercase tracking-wider text-center mb-6">
        Coverage For:
      </p>
      <div className="space-y-3">
        {coverageItems.map((item) => (
          <CoverageAccordionItem
            key={item.id}
            item={item}
            isOpen={openId === item.id}
            onToggle={() => setOpenId(openId === item.id ? null : item.id)}
          />
        ))}
      </div>
    </div>
  );
};

const WhoIHelp = () => {
  return (
    <section className="py-20 lg:py-28 bg-[#F9F7F4]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-16">
          <span className="text-sm font-semibold text-[#3A7D44] uppercase tracking-wider">
            Who I Help
          </span>
          <h2
            className="text-3xl sm:text-4xl font-bold text-[#1B2A1B] mt-3 mb-4"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          >
            You work for yourself.
            <br />
            <span className="italic text-[#3A7D44]" style={{ fontStyle: 'italic' }}>Your insurance should work for you.</span>
          </h2>
          <p className="text-[#5A6B5A] leading-relaxed">
            Whether you're running a business, working for yourself, or taking care
            of your family, I help you find coverage that actually fits your life.
          </p>
        </div>

        {/* Qualification Statements */}
        <div className="max-w-3xl mx-auto space-y-6">
          {qualificationStatements.map((statement, index) => (
            <div
              key={`qual-${index}`}
              data-testid={`qualification-statement-${index + 1}`}
              className="flex items-start gap-4 bg-white rounded-xl p-6 border border-gray-100 shadow-sm"
            >
              <CheckCircle2
                size={28}
                className="text-[#3A7D44] flex-shrink-0 mt-0.5"
              />
              <p className="text-base sm:text-lg text-[#1B2A1B] leading-relaxed">
                {statement}
              </p>
            </div>
          ))}

          {/* Small note */}
          <div
            data-testid="group-benefits-note"
            className="mt-8 border-l-4 border-[#3A7D44]/40 pl-5 py-2"
          >
            <p className="text-sm text-[#5A6B5A] leading-relaxed italic">
              If you're looking for group benefits through an employer, I can point
              you in the right direction — this service is built specifically for
              independent professionals and small business owners.
            </p>
          </div>
        </div>

        {/* Coverage Accordion */}
        <CoverageAccordion />
      </div>
    </section>
  );
};

export default WhoIHelp;
